import Logo from "~/components/Logo"
import {
  articleBlurb,
  articleBodyStyle,
  articleDetails,
  articleSection,
  articleTitle,
  h1Style,
  h1Wrap,
  h2Style,
  headerTextStyle,
  imagePreviewBlog,
  shrinkingGrid2,
  shrinkingGridItem,
  shrinkingGridItemTitle,
} from "./article.css"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import { Link } from "@remix-run/react"
import Footer from "~/components/Footer"
import { MetaFunction } from "@remix-run/node"

export const meta: MetaFunction = () => {
  return [
    {
      title: "Rescape AI's Garden Design Guide",
    },
    {
      name: "description",
      content:
        "Discover a complete garden design guide featuring classic and modern styles, practical layout tips, and insights into AI gardening tools.",
    },
    {
      property: "og:title",
      content: "Rescape AI's Garden Design Guide",
    },
    {
      property: "og:description",
      content:
        "Discover a complete garden design guide featuring classic and modern styles, practical layout tips, and insights into AI gardening tools.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/guide/garden-5-after-ai-contemporary-design.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-guide",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Rescape AI's Garden Design Guide | Released Weekly",
        description:
          "Discover a complete garden design guide featuring classic and modern styles, practical layout tips, and insights into AI gardening tools.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-11-14",
        dateModified: "2025-11-14",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/guide/garden-5-after-ai-contemporary-design.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/garden-design-guide",
        },
        articleSection: [
          "Technology",
          "Garden Design Guide",
          "Artificial Intelligence",
          "Gardening",
        ],
      },
    },
  ]
}

function GardenDesignGuide() {
  const pages = [
    {
      header: "Contemporary",
      description:
        "Defined by their geometric layouts and minimal colour schemes. Plants are chosen for their colour and texture while fences and walls stick to white, black, grey or natural wood. Learn all about Contemporary gardens here and how to visualise your ideal garden with Rescape AI.",
    },
    {
      title: "Contemporary Materials and Plants",
      slug: "contemporary/materials-and-plants",
      description:
        "Learn about the use of clean lines, neutral colours and geometric layouts to create a structured modern garden.",
      url: "/guide/contemporary-dark-fence.webp",
    },
    {
      title: "Features of Contemporary Gardens",
      slug: "contemporary/features-of-contemporary-gardens",
      description:
        "Discover the key features of contemporary gardens, including minimalist layouts and modern materials.",
      url: "/guide/architectural-plants.webp",
    },
    {
      title: "Contemporary Garden Ideas using AI",
      slug: "contemporary/contemporary-garden-ideas-using-ai",
      description:
        "See examples of how Rescape AI completely renovates gardens for our customers, creating contemporary outdoor spaces.",
      url: "/guide/garden-5-after-ai-contemporary-design.webp",
    },
    {
      header: "Japanese Zen",
      description:
        "Japanese Zen gardens (karesansui) are tranquil spaces designed for reflection, meditation, and mindfulness.",
    },
    {
      title: "Japanese Zen Materials and Plants",
      slug: "zen/materials-and-plants",
      description:
        "Japanese Zen gardens embrace simplicity, harmony, and mindfulness. Unlike traditional gardens focused on lush planting or colourful displays, Zen gardens use minimalism, symbolism, and natural elements to evoke peace and contemplation.",
      url: "/guide/red-maple-zen.webp",
    },
    {
      title: "Features of Zen Gardens",
      slug: "zen/features-of-zen-gardens",
      description:
        "Explore the essential features of Japanese Zen gardens. Learn about rocks, raked sand, moss, and other elements that create serene spaces.",
      url: "/guide/raked-sand.webp",
    },
    {
      title: "Zen Garden Ideas using AI",
      slug: "zen/zen-garden-ideas-using-ai",
      description:
        "Utilising AI to design a Zen garden is incredibly easy. Learn about how Rescape AI generates realistic garden designs for you all from a photo upload.",
      url: "/guide/zen-5.webp",
    },
    {
      header: "Cottage",
      description:
        "Cottage gardens are charming, informal spaces filled with overflowing blooms, winding paths, and a relaxed, storybook atmosphere.",
    },
    {
      title: "Features of Cottage Gardens",
      slug: "cottage/features-of-cottage-gardens",
      description:
        "Explore defining cottage garden design features, from overflowing floral borders and natural materials to informal layouts.",
      url: "/articles/cottage-garden-unstructured.webp",
    },
    {
      title: "Materials and Plants for Cottage Gardens",
      slug: "cottage/materials-and-plants",
      description:
        "Cottage gardens celebrate romance, informality, and abundance. Cottage gardens overflow with colour, texture, and life.",
      url: "/articles/cottage-garden-bench.webp",
    },
    {
      title: "Cottage Garden Design Ideas",
      slug: "cottage/cottage-garden-ideas-using-ai",
      description:
        "See how you can use Rescape AI's garden design tool to visualise your dream Cottage Garden.",
      url: "/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp",
    },
    {
      header: "Urban Gardens",
      description:
        "Urban living doesn't have to mean less time spent outdoors. There are style choices and techniques to combat issues like privacy concerns. Read here to learn about how to design your urban garden with these design principles in mind.",
    },
    {
      title: "Features of Urban Gardens",
      slug: "urban/features-of-urban-gardens",
      description:
        "Learn the features of urban gardens. Rescape AI is one of the styles that it can generate beautiful garden images with",
      url: "/guide/living-wall-2.webp",
    },
    {
      title: "Urban Gardens Materials and Plants",
      slug: "urban/materials-and-plants",
      description:
        "Urban gardens may be modest in size — but with the right design approach, materials and plants, they can become verdant, useful, and deeply rewarding parts of city living.",
      url: "/guide/steel-container-urban.jpg",
    },
  ]
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/ai-garden-design/cozy-balcony-ai-generated.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Garden Design Guide from Rescape AI</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={articleSection}>
          <ul className={shrinkingGrid2}>
            {pages.map((article) => {
              if (article.header) {
                return (
                  <div key={article.header} className={shrinkingGridItemTitle}>
                    <h2 className={h2Style}>{article.header}</h2>
                    <p className={articleBodyStyle}>{article.description}</p>
                  </div>
                )
              }
              return (
                <li key={article.slug} className={shrinkingGridItem}>
                  <Link
                    title={article.title}
                    to={`/garden-design-guide/${article.slug}`}
                    style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                  >
                    <img src={article.url} className={imagePreviewBlog} />
                    <span className={articleDetails}>
                      <h2
                        className={articleTitle}
                        style={{
                          textTransform: "capitalize",
                        }}
                      >
                        {article.title}
                      </h2>
                      <p className={articleBlurb}>{article.description}</p>
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </section>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default GardenDesignGuide
